/**
 * AccessibilityPanel.jsx
 * 
 * MeitY-style floating accessibility panel with:
 * - Dark/High Contrast toggle
 * - Invert Colors toggle  
 * - Saturation control
 * - Text size controls
 * - Highlight Links toggle
 * - Hide Images toggle
 * - Large Cursor toggle
 * - Reset All button
 * 
 * Uses localStorage for persistence.
 * Panel is overlay-based with position: fixed (no layout impact).
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Accessibility, Sun, Moon, Eye, Type, Link2, Image, MousePointer2, RotateCcw } from 'lucide-react';

// Default accessibility settings
const DEFAULT_SETTINGS = {
    highContrast: false,
    invertColors: false,
    lowSaturation: false,
    fontSize: 100, // percentage
    highlightLinks: false,
    hideImages: false,
    largeCursor: false,
};

// localStorage key
const STORAGE_KEY = 'cish-accessibility';

/**
 * Accessibility Panel Component
 * Floating overlay panel for accessibility controls
 */
export default function AccessibilityPanel() {
    const [isOpen, setIsOpen] = useState(false);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const panelRef = useRef(null);

    // Load settings from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setSettings({ ...DEFAULT_SETTINGS, ...parsed });
            } catch (e) {
                console.warn('Failed to parse accessibility settings:', e);
            }
        }
    }, []);

    // Apply settings to DOM whenever they change
    useEffect(() => {
        const html = document.documentElement;

        // High Contrast
        html.classList.toggle('a11y-high-contrast', settings.highContrast);

        // Invert Colors
        html.classList.toggle('a11y-invert-colors', settings.invertColors);

        // Low Saturation
        html.classList.toggle('a11y-low-saturation', settings.lowSaturation);

        // Font Size
        html.style.fontSize = `${settings.fontSize}%`;

        // Highlight Links
        html.classList.toggle('a11y-highlight-links', settings.highlightLinks);

        // Hide Images
        html.classList.toggle('a11y-hide-images', settings.hideImages);

        // Large Cursor
        html.classList.toggle('a11y-large-cursor', settings.largeCursor);

        // Save to localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    }, [settings]);

    // Close panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                // Check if click was on the trigger button
                const trigger = document.querySelector('[data-a11y-trigger]');
                if (trigger && trigger.contains(event.target)) return;
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Toggle a boolean setting
    const toggleSetting = (key) => {
        setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // Adjust font size
    const adjustFontSize = (action) => {
        setSettings((prev) => {
            let newSize = prev.fontSize;
            if (action === 'increase' && newSize < 150) newSize += 10;
            if (action === 'decrease' && newSize > 80) newSize -= 10;
            if (action === 'reset') newSize = 100;
            return { ...prev, fontSize: newSize };
        });
    };

    // Reset all settings
    const resetAll = () => {
        setSettings(DEFAULT_SETTINGS);
        document.documentElement.style.fontSize = '100%';
    };

    return (
        <>
            {/* Accessibility Trigger Button - Fixed position */}
            <button
                data-a11y-trigger
                onClick={() => setIsOpen(!isOpen)}
                className="accessibility-trigger"
                aria-label="Accessibility Options"
                aria-expanded={isOpen}
                title="Accessibility Options"
            >
                <Accessibility className="w-5 h-5" />
            </button>

            {/* Accessibility Panel - Fixed overlay */}
            {isOpen && (
                <div
                    ref={panelRef}
                    className="accessibility-panel"
                    role="dialog"
                    aria-label="Accessibility Controls"
                >
                    {/* Panel Header */}
                    <div className="accessibility-panel-header">
                        <div className="flex items-center gap-2">
                            <Accessibility className="w-5 h-5" />
                            <span className="font-semibold">Accessibility Controls</span>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="accessibility-close-btn"
                            aria-label="Close accessibility panel"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Panel Content - Grid of controls */}
                    <div className="accessibility-panel-content">
                        {/* Row 1: Contrast & Invert */}
                        <button
                            onClick={() => toggleSetting('highContrast')}
                            className={`accessibility-option ${settings.highContrast ? 'active' : ''}`}
                            aria-pressed={settings.highContrast}
                        >
                            <Sun className="w-6 h-6" />
                            <span>DARK CONTRAST</span>
                        </button>

                        <button
                            onClick={() => toggleSetting('invertColors')}
                            className={`accessibility-option ${settings.invertColors ? 'active' : ''}`}
                            aria-pressed={settings.invertColors}
                        >
                            <Moon className="w-6 h-6" />
                            <span>INVERT</span>
                        </button>

                        {/* Row 2: Saturation & Text Size+ */}
                        <button
                            onClick={() => toggleSetting('lowSaturation')}
                            className={`accessibility-option ${settings.lowSaturation ? 'active' : ''}`}
                            aria-pressed={settings.lowSaturation}
                        >
                            <Eye className="w-6 h-6" />
                            <span>SATURATION</span>
                        </button>

                        <button
                            onClick={() => adjustFontSize('increase')}
                            className="accessibility-option"
                            aria-label="Increase text size"
                        >
                            <Type className="w-6 h-6" />
                            <span>TEXT SIZE +</span>
                        </button>

                        {/* Row 3: Text Size- & Highlight Links */}
                        <button
                            onClick={() => adjustFontSize('decrease')}
                            className="accessibility-option"
                            aria-label="Decrease text size"
                        >
                            <span className="text-2xl font-bold">A-</span>
                            <span>TEXT SIZE -</span>
                        </button>

                        <button
                            onClick={() => toggleSetting('highlightLinks')}
                            className={`accessibility-option ${settings.highlightLinks ? 'active' : ''}`}
                            aria-pressed={settings.highlightLinks}
                        >
                            <Link2 className="w-6 h-6" />
                            <span>HIGHLIGHT LINKS</span>
                        </button>

                        {/* Row 4: Hide Images & Cursor */}
                        <button
                            onClick={() => toggleSetting('hideImages')}
                            className={`accessibility-option ${settings.hideImages ? 'active' : ''}`}
                            aria-pressed={settings.hideImages}
                        >
                            <Image className="w-6 h-6" />
                            <span>HIDE IMAGES</span>
                        </button>

                        <button
                            onClick={() => toggleSetting('largeCursor')}
                            className={`accessibility-option ${settings.largeCursor ? 'active' : ''}`}
                            aria-pressed={settings.largeCursor}
                        >
                            <MousePointer2 className="w-6 h-6" />
                            <span>{settings.largeCursor ? 'LARGE CURSOR' : 'DEFAULT CURSOR'}</span>
                        </button>
                    </div>

                    {/* Reset All Button */}
                    <div className="accessibility-panel-footer">
                        <button
                            onClick={resetAll}
                            className="accessibility-reset-btn"
                            aria-label="Reset all accessibility settings"
                        >
                            <RotateCcw className="w-4 h-4" />
                            <span>Reset All Settings</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
